import { Box, Container, LinearProgress, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/Page';
import { useListCodes, useDeleteCode } from 'src/hooks/codes';
import { usePagination } from 'src/hooks/pagination';
import { setFilters } from 'src/store/reducers/lists';
import Results from './components/Results';
import Toolbar from './components/Toolbar';
import AlertDialog from 'src/components/AlertDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
}));

const CodesListView = () => {
  const classes = useStyles();
  const { t } = useTranslation('codes');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [deleteModal, setDeleteModal] = useState({ show: false, codeId: null });
  const filters = useSelector((state) => state.lists.codes?.filters);
  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    resetPage,
    fitsInPage,
    navigateToLastPage,
  } = usePagination(filters?.page, filters?.pageSize);
  const [company, setCompany] = useState(filters?.company || null);
  const [sort, setSort] = useState(filters?.sort || '');
  const query = { page, pageSize, sort, companyId: company?.id, include: 'company' };
  const { isFetching, resolvedData: codes, refetch, isError } = useListCodes(query);
  const [
    deleteCode,
    { isLoading: isLoadingDelete, data: dataDelete, isError: isErrorDelete, error: errorDelete },
  ] = useDeleteCode();

  if (codes?.data.count && !fitsInPage(codes.data.count)) {
    navigateToLastPage(codes.data.count);
  }

  const handleDeleteCode = () => {
    setDeleteModal({ show: false, codeId: null });
    deleteCode(deleteModal.codeId);
  };

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t('modals.error.title_list_codes'), { variant: 'error' });
    }
  }, [enqueueSnackbar, isError, t]);

  useEffect(() => {
    dispatch(
      setFilters({
        list: 'codes',
        filters: { page, pageSize, sort, company },
      }),
    );
  }, [dispatch, page, pageSize, sort, company]);

  useEffect(() => {
    if (!isLoadingDelete && dataDelete && !isErrorDelete) {
      enqueueSnackbar(t('modals.delete_code.success.text'), { variant: 'success' });

      refetch();
    }
  }, [dataDelete, enqueueSnackbar, isErrorDelete, isLoadingDelete, refetch, t]);

  return (
    <Page className={classes.root}>
      <Container maxWidth={false}>
        <Toolbar company={company} setCompany={setCompany} resetPage={resetPage} />
        <Box mt={3}>
          {isFetching && <LinearProgress color="primary" />}
          <Results
            codes={codes?.data?.rows}
            page={page}
            setPage={setPage}
            resetPage={resetPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            sort={sort}
            setSort={setSort}
            isLoading={isFetching}
            deleteCode={(codeId) => setDeleteModal({ show: true, codeId })}
          />

          {deleteModal.show && (
            <AlertDialog
              title={t('modals.delete_code.confirmation.title')}
              text={t('modals.delete_code.confirmation.text')}
              type="warning"
              showAcceptButton={false}
              showDeleteButton={true}
              deleteAction={handleDeleteCode}
              showCancelButton={true}
              cancelAction={() => setDeleteModal({ show: false, codeId: null })}
            />
          )}
          {isErrorDelete && (
            <AlertDialog
              title={t('modals.delete_code.error.title')}
              text={errorDelete?.message || t('modals.delete_code.error.text')}
              type="error"
            />
          )}
        </Box>
      </Container>
    </Page>
  );
};

export default CodesListView;
